import idb from "@/plugins/indexdb.js";
import ProjectsRepository, {
  fetchProjects,
  fetchProject,
  updateProject,
  createProject
} from "../../repositories/projectsRepository";

// Initial state
function initialProjectState() {
  return {
    agency_id: null,
    applicator_id: null,
    applicator_supp_id: null,
    idc_launch_test: false,
    center: null,
    comment: "",
    file_number: "",
    fitting1_at: "",
    fitting2_at: "",
    fitting3_at: "",
    fitting_return1_at: "",
    fitting_return2_at: "",
    fitting_return3_at: "",
    initial_transfer_at: "",
    molding_arrival_at: "",
    patient_uuid: "",
    planned_delivery_at: "",
    product_model: "",
    product_uuid: "",
    project_method: "",
    real_delivery_at: null,
    renew: "",
    server_version: "",
    transfer1_at: "",
    transfer2_at: "",
    transfer3_at: "",
    uuid: "",
    workshop_exit1_at: "",
    workshop_exit2_at: "",
    workshop_exit3_at: "",
    workshop_exit_at: "",
    transferable: false
  };
}

export default {
  namespaced: true,
  state: {
    projects: [],
    project: initialProjectState(),

    operationError: null,
    isProcessing: false,

    loading: false,
    data: [],
    error: null,

    pagination: {
      page: 1,
      itemsPerPage: 500,
      sortBy: ["created_at"],
      sortDesc: [true]
    }
  },
  mutations: {
    PROJECTS_DATA_REQUEST(state) {
      Object.assign(state, { data: null, loading: true, error: null });
    },

    PROJECTS_DATA_ERROR(state, payload) {
      Object.assign(state, {
        loading: false,
        data: [],
        error: payload || true
      });
    },

    PROJECTS_DATA_SUCCESS(state, payload) {
      Object.assign(state, {
        loading: false,
        projects: payload.data,
        data: payload
      });
    },

    UPDATE_PAGINATION(state, options) {
      Object.assign(state.pagination, options);
    },

    // Set projects
    setProjects(state, projects) {
      state.projects = projects;
    },

    // Set project
    setProject(state, project) {
      state.project = project;
    },

    // Reset project to his initial state
    RESET_PROJECT(state) {
      state.project = initialProjectState();
    }
  },
  actions: {
    // Fetches projects
    async fetchProjects({ commit }, params) {
      commit("PROJECTS_DATA_REQUEST");

      // Manage sorting
      if (params.sortBy) {
        if (params.sortBy.length === 1 && params.sortDesc.length === 1) {
          params.sortBy = params.sortBy[0];
          params.sortType = params.sortDesc[0] ? "desc" : "asc";
        }
      }

      delete params.sortDesc; // Delete useless prop

      const data = await fetchProjects(params);

      if (data) {
        commit("PROJECTS_DATA_SUCCESS", data);
      }

      return data;
    },

    fetchProject(context, uuid) {
      return fetchProject(uuid);
    },

    // Reset project state
    resetProject({ commit }) {
      commit("RESET_PROJECT");
    },

    /**
     * Set project as delivered
     *
     * @param {String} project_uuid Project uuid
     */
    async setDeliveredProject(context, project_uuid) {
      try {
        return await ProjectsRepository.setDelivery(project_uuid);
      } catch (error) {
        throw new Error("Projet déjà livré");
      }
    },

    /**
     * Get projects from API
     */
    async getProjects() {
      try {
        const { data } = await ProjectsRepository.get();

        console.log(data);

        // Order by created_at
        const projects = data.sort(function(a, b) {
          a = new Date(a.created_at);
          b = new Date(b.created_at);
          return a > b ? -1 : a < b ? 1 : 0;
        });

        // Delete all locked projects from local
        projects.forEach(async project => {
          const localProject = await idb.db.projects.get(project.uuid);
          if (project.idc_locked && localProject) {
            await idb.db.projects.delete(localProject.uuid);
          }
        });

        return projects;
      } catch (error) {
        throw error;
      }
    },

    // Get all projects from IndexedDB
    getLocalProjects() {
      return new Promise((resolve, reject) => {
        idb.db.projects
          .toArray()
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    },

    // Create project in API
    async createProject({ commit }, payload) {
      const result = await createProject(payload).catch(e => {
        commit("PROJECTS_DATA_ERROR", e);
        return Promise.reject(e);
      });

      return result;
    },

    // Update project in API
    async updateProject({ commit }, { uuid, payload }) {
      const result = await updateProject(uuid, payload).catch(e => {
        commit("PROJECTS_DATA_ERROR", e);
      });

      return result;
    },

    // Store a project in local DB
    createLocalProject(context, payload) {
      return new Promise((resolve, reject) => {
        idb.db.projects
          .add(payload)
          .then(res => {
            resolve(res);
          })
          .catch(err => {
            reject(err);
          });
      });
    },

    // Get project from API
    async getProject(context, uuid) {
      try {
        const { data } = await ProjectsRepository.getProject(uuid);
        return data;
      } catch (error) {
        if (error.response && error.response.status == 404) {
          return undefined;
        }

        throw error;
      }
    },

    // Get local project from IndexedDB
    async getLocalProject(context, project_uuid) {
      const project = await idb.db.projects.get(project_uuid);

      // Throw an error if product not found
      if (!project) {
        throw new Error(
          `Project not found in IndexedDB. UUID: ${project_uuid}`
        );
      }

      return project;
    },

    // Delete local project from IndexedDB
    async deleteLocalProject(context, project_uuid) {
      var project = await idb.db.projects.get(project_uuid);

      // Throw an error if product not found
      if (!project) {
        throw new Error(
          `Project not found in IndexedDB. UUID: ${project_uuid}`
        );
      }

      await idb.db.projects.delete(project_uuid);
    },

    // Medias

    // Creates a media in IndexedDB
    async createMediaIndexedDb(context, media) {
      try {
        return await idb.db.medias.put(media);
      } catch (error) {
        throw error;
      }
    },

    // Gets project medias from IndexedDB
    async getProjectMediasFromIndexedDb(context, project_uuid) {
      try {
        const collection = idb.db.medias.where({
          project_uuid: project_uuid
        });
        return collection.toArray();
      } catch (error) {
        throw error;
      }
    },

    // Create project media in API
    async createProjectMedia(context, { project_uuid, media_uuid, payload }) {
      const { data } = await ProjectsRepository.createSketchMedia(
        project_uuid,
        media_uuid,
        payload
      );
      return data;
    },

    // Deletes project media in API
    async deleteProjectMedia(context, { project_uuid, media_uuid }) {
      const { data } = await ProjectsRepository.deleteProjectMedia(
        project_uuid,
        media_uuid
      );
      return data;
    },

    // Gets project media from API
    async getProjectMedia(context, { project_uuid, media_uuid }) {
      const { data } = await ProjectsRepository.getMedia(
        project_uuid,
        media_uuid
      );
      return data;
    },

    // Gets project media image from API
    async getProjectMediaImage(context, { project_uuid, media_uuid }) {
      const { data } = await ProjectsRepository.getMediaImage(
        project_uuid,
        media_uuid
      );
      return data;
    },

    // Create project media image in API
    async createProjectMediaImage(
      context,
      { project_uuid, media_uuid, payload }
    ) {
      try {
        const { data } = await ProjectsRepository.createProjectMediaImage(
          project_uuid,
          media_uuid,
          payload
        );
        return data;
      } catch (error) {
        throw error;
      }
    },

    // Clean store
    async cleanProjects({ commit }) {
      commit("setProjects", []);
      return;
    }
  },

  getters: {
    totalProjects: state => {
      if (!state.data || !state.data.meta) {
        return 0;
      }

      return state.data.meta.total;
    },

    getProjectByUuid: state => uuid => {
      return state.projects.find(item => item.uuid === uuid);
    }
  }
};
